import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useSurfaces, useCreateSurface, useRetireSurface } from "../hooks/useSurfaces";
import { useIdentities } from "../hooks/useSurfaces";
import { WorkspaceHeader } from "../components/layout/WorkspaceHeader";
import { Input } from "../components/ui/Input";
import type {
  BrandSurface,
  SurfaceCategory,
  SurfaceControlLevel,
  ParticipatoryIdentityConfig,
} from "../../../shared/types";

const CATEGORY_LABELS: Record<SurfaceCategory, string> = {
  owned_publication: "Owned Publication",
  participatory_identity: "Participatory Identity",
  hosted_listing: "Hosted Listing",
  earned_placement: "Earned Placement",
  algorithmic_discovery: "Algorithmic Discovery",
};

const CATEGORY_DESCRIPTIONS: Record<SurfaceCategory, string> = {
  owned_publication: "Channels you own and control",
  participatory_identity: "Communities where you participate",
  hosted_listing: "Third-party directories and listings",
  earned_placement: "PR, mentions, and backlinks",
  algorithmic_discovery: "Search and algorithmic visibility",
};

const STATUS_STYLES: Record<string, string> = {
  proposed: "bg-slate-100 text-slate-700",
  active: "bg-emerald-100 text-emerald-700",
  warming_up: "bg-amber-100 text-amber-700",
  paused: "bg-yellow-100 text-yellow-700",
  blocked: "bg-red-100 text-red-700",
  retired: "bg-gray-100 text-gray-500",
};

const HEALTH_STYLES: Record<string, string> = {
  unknown: "bg-gray-100 text-gray-500",
  healthy: "bg-emerald-100 text-emerald-700",
  attention: "bg-amber-100 text-amber-700",
  blocked: "bg-red-100 text-red-700",
};

const CONTROL_LEVEL_LABELS: Record<string, string> = {
  owned: "Owned",
  claimed: "Claimed",
  participated: "Participated",
  observed: "Observed",
  earned: "Earned",
};

// Kinds available for each category
const KINDS_BY_CATEGORY: Record<SurfaceCategory, string[]> = {
  owned_publication: ["website", "blog", "newsletter", "youtube", "podcast", "substack"],
  participatory_identity: ["bluesky", "reddit", "hacker_news", "quora", "linkedin_community", "indiehackers"],
  hosted_listing: ["g2", "capterra", "producthunt", "yelp", "google_business"],
  earned_placement: ["press_mention", "backlink", "podcast_guest", "newsletter_feature"],
  algorithmic_discovery: ["serp", "app_store", "youtube_search", "reddit_search"],
};

function formatDate(date: string | null): string {
  if (!date) return "Never";
  return new Date(date).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function SurfaceCard({
  surface,
  isChild,
  parentName,
  onClick,
}: {
  surface: BrandSurface;
  isChild?: boolean;
  parentName?: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full cursor-pointer rounded-lg border bg-white p-4 text-left transition-shadow hover:shadow-md focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
        isChild ? "ml-6 border-l-4 border-l-slate-300" : "border-slate-200"
      }`}
    >
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <span className="rounded bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-700">
              {surface.kind}
            </span>
            <span
              className={`rounded px-2 py-0.5 text-xs font-medium ${
                STATUS_STYLES[surface.status] ?? "bg-gray-100 text-gray-600"
              }`}
            >
              {surface.status}
            </span>
            <span
              className={`rounded px-2 py-0.5 text-xs font-medium ${
                HEALTH_STYLES[surface.health_status] ?? "bg-gray-100 text-gray-600"
              }`}
            >
              {surface.health_status}
            </span>
          </div>

          <h3 className="mt-2 text-base font-semibold text-slate-900">
            {surface.display_name}
          </h3>

          {isChild && parentName && (
            <p className="mt-1 text-xs text-slate-500">Parent: {parentName}</p>
          )}

          <div className="mt-2 flex items-center gap-3 text-xs text-slate-500">
            <span className="rounded bg-slate-50 px-1.5 py-0.5">
              {CONTROL_LEVEL_LABELS[surface.control_level] ?? surface.control_level}
            </span>
            <span>Last activity: {formatDate(surface.last_activity_at)}</span>
          </div>

        </div>

        <div className="ml-4 text-right">
          <span className="text-xs text-slate-400">Priority: {surface.priority}</span>
        </div>
      </div>
    </button>
  );
}

function SurfaceDetail({
  brandId,
  surface,
  onClose,
  onRetire,
}: {
  brandId: string;
  surface: BrandSurface;
  onClose: () => void;
  onRetire: () => void;
}) {
  const config = surface.config as ParticipatoryIdentityConfig;
  const isCat2 = surface.category === "participatory_identity";

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-6">
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-2">
            <span className="rounded bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-700">
              {surface.kind}
            </span>
            <span
              className={`rounded px-2 py-0.5 text-xs font-medium ${
                STATUS_STYLES[surface.status] ?? "bg-gray-100 text-gray-600"
              }`}
            >
              {surface.status}
            </span>
          </div>
          <h2 className="mt-2 text-xl font-semibold text-slate-900">
            {surface.display_name}
          </h2>
          <p className="text-sm text-slate-500">{CATEGORY_LABELS[surface.category]}</p>
        </div>
        <button
          onClick={onClose}
          className="rounded-lg p-2 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
        >
          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <div className="mt-6 grid grid-cols-2 gap-4">
        <div className="rounded-lg bg-slate-50 p-3">
          <p className="text-xs text-slate-500">Control Level</p>
          <p className="text-sm font-medium text-slate-900">
            {CONTROL_LEVEL_LABELS[surface.control_level] ?? surface.control_level}
          </p>
        </div>
        <div className="rounded-lg bg-slate-50 p-3">
          <p className="text-xs text-slate-500">Health Status</p>
          <p className="text-sm font-medium text-slate-900">{surface.health_status}</p>
        </div>
        <div className="rounded-lg bg-slate-50 p-3">
          <p className="text-xs text-slate-500">Priority</p>
          <p className="text-sm font-medium text-slate-900">{surface.priority}</p>
        </div>
        <div className="rounded-lg bg-slate-50 p-3">
          <p className="text-xs text-slate-500">Last Observed</p>
          <p className="text-sm font-medium text-slate-900">
            {formatDate(surface.last_observed_at)}
          </p>
        </div>
      </div>

      {surface.capabilities && surface.capabilities.length > 0 && (
        <div className="mt-6">
          <h4 className="text-sm font-medium text-slate-900">Capabilities</h4>
          <div className="mt-2 flex flex-wrap gap-2">
            {surface.capabilities.map((cap) => (
              <span
                key={cap}
                className="rounded-full bg-indigo-50 px-3 py-1 text-xs font-medium text-indigo-700"
              >
                {cap}
              </span>
            ))}
          </div>
        </div>
      )}

      {surface.canonical_url && (
        <div className="mt-6">
          <h4 className="text-sm font-medium text-slate-900">URL</h4>
          <a
            href={surface.canonical_url}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-1 text-sm text-indigo-600 hover:underline"
          >
            {surface.canonical_url}
          </a>
        </div>
      )}

      {isCat2 && config && (config.karma !== undefined || config.account_age_days !== undefined || config.max_posts_per_day !== undefined) && (
        <div className="mt-6">
          <h4 className="text-sm font-medium text-slate-900">Account details</h4>
          <div className="mt-2 rounded-lg bg-slate-50 p-3">
            <div className="grid grid-cols-2 gap-3 text-sm">
              {config.karma !== undefined && (
                <div>
                  <span className="text-slate-500">Karma:</span>{" "}
                  <span className="font-medium">{config.karma}</span>
                </div>
              )}
              {config.account_age_days !== undefined && (
                <div>
                  <span className="text-slate-500">Account age:</span>{" "}
                  <span className="font-medium">{config.account_age_days} days</span>
                </div>
              )}
              {config.max_posts_per_day !== undefined && (
                <div>
                  <span className="text-slate-500">Max posts/day:</span>{" "}
                  <span className="font-medium">{config.max_posts_per_day}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {surface.notes && (
        <div className="mt-6">
          <h4 className="text-sm font-medium text-slate-900">Notes</h4>
          <p className="mt-1 text-sm text-slate-600">{surface.notes}</p>
        </div>
      )}

      {(surface.kind === "website" || (surface.kind === "bluesky" && surface.status === "active")) && surface.canonical_url && (
        <Link to={`/brand/${brandId}/settings/surfaces/${surface.id}/content`} className="mt-6 inline-flex rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800">
          {surface.kind === "bluesky" ? "View posts and profile" : "View website pages"}
        </Link>
      )}

      <div className="mt-6 flex gap-3">
        <button
          onClick={onRetire}
          className="rounded-lg bg-red-50 px-4 py-2 text-sm font-medium text-red-700 hover:bg-red-100"
        >
          Retire Surface
        </button>
      </div>
    </div>
  );
}

function AddSurfaceFlow({
  brandId,
  onClose,
  onSuccess,
}: {
  brandId: string;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [step, setStep] = useState<"category" | "kind" | "details">("category");
  const [selectedCategory, setSelectedCategory] = useState<SurfaceCategory | null>(null);
  const [selectedKind, setSelectedKind] = useState<string>("");
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    display_name: "",
    canonical_url: "",
    external_id: "",
    notes: "",
  });

  const createSurface = useCreateSurface(brandId);
  const isBluesky = selectedKind === "bluesky";

  const handleSubmit = async () => {
    if (!selectedCategory || !selectedKind) return;

    const profileInput = formData.canonical_url.trim();
    if (isBluesky && !profileInput) {
      setSubmitError("Enter a Bluesky handle or public profile URL.");
      return;
    }

    setSubmitError(null);

    const controlLevel: SurfaceControlLevel =
      isBluesky
        ? "observed"
        : selectedCategory === "owned_publication"
        ? "owned"
        : selectedCategory === "participatory_identity"
        ? "participated"
        : selectedCategory === "hosted_listing"
        ? "claimed"
        : selectedCategory === "earned_placement"
        ? "earned"
        : "observed";

    // The surface contract still requires warm_up_phase for participatory
    // identities, but nothing gates on it, so it is not offered as a control.
    const config: Record<string, unknown> =
      selectedCategory === "participatory_identity"
        ? { warm_up_phase: "cold" }
        : {};

    try {
      await createSurface.mutateAsync({
        kind: selectedKind,
        category: selectedCategory,
        display_name: formData.display_name.trim(),
        control_level: controlLevel,
        canonical_url: profileInput || null,
        external_id: isBluesky ? null : formData.external_id.trim() || null,
        notes: formData.notes.trim() || null,
        config,
      });

      onSuccess();
    } catch (error) {
      setSubmitError(
        error instanceof Error ? error.message : "Could not create this surface. Please try again.",
      );
    }
  };

  if (step === "category") {
    return (
      <div className="rounded-[20px] border border-slate-200 bg-white p-6 shadow-sm">
        <h3 className="text-lg font-semibold text-slate-900">Add Surface</h3>
        <p className="mt-1 text-sm text-slate-500">Select a category</p>

        <div className="mt-4 grid gap-3">
          {(Object.keys(CATEGORY_LABELS) as SurfaceCategory[]).map((cat) => (
            <button
              key={cat}
              onClick={() => {
                setSelectedCategory(cat);
                setSubmitError(null);
                setStep("kind");
              }}
              className="flex flex-col rounded-lg border border-slate-200 p-4 text-left transition-colors hover:border-brand-300 hover:bg-brand-50"
            >
              <span className="font-medium text-slate-900">{CATEGORY_LABELS[cat]}</span>
              <span className="text-sm text-slate-500">{CATEGORY_DESCRIPTIONS[cat]}</span>
            </button>
          ))}
        </div>

        <button
          onClick={onClose}
          className="mt-4 rounded-lg px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100"
        >
          Cancel
        </button>
      </div>
    );
  }

  if (step === "kind") {
    const kinds = selectedCategory ? KINDS_BY_CATEGORY[selectedCategory] : [];
    return (
      <div className="rounded-[20px] border border-slate-200 bg-white p-6 shadow-sm">
        <h3 className="text-lg font-semibold text-slate-900">Add Surface</h3>
        <p className="mt-1 text-sm text-slate-500">
          Select a kind for {selectedCategory && CATEGORY_LABELS[selectedCategory]}
        </p>

        <div className="mt-4 grid gap-2">
          {kinds.map((kind) => (
            <button
              key={kind}
              onClick={() => {
                setSelectedKind(kind);
                setSubmitError(null);
                setStep("details");
              }}
              className="rounded-lg border border-slate-200 p-3 text-left font-medium text-slate-900 transition-colors hover:border-brand-300 hover:bg-brand-50"
            >
              {kind === "bluesky" ? "Bluesky" : kind}
            </button>
          ))}
        </div>

        <div className="mt-4 flex gap-3">
          <button
            onClick={() => setStep("category")}
            className="rounded-lg px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100"
          >
            Back
          </button>
          <button
            onClick={onClose}
            className="rounded-lg px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100"
          >
            Cancel
          </button>
        </div>
      </div>
    );
  }

  return (
      <div className="rounded-[20px] border border-slate-200 bg-white p-6 shadow-sm">
      <h3 className="text-lg font-semibold text-slate-900">Add Surface</h3>
      <p className="mt-1 text-sm text-slate-500">
        {isBluesky ? "Bluesky" : selectedKind} ({selectedCategory && CATEGORY_LABELS[selectedCategory]})
      </p>

      <div className="mt-4 space-y-4">
        <div>
          <label htmlFor="surface-display-name" className="block text-sm font-medium text-slate-700">
            Display Name <span className="text-red-500">*</span>
          </label>
          <Input
            id="surface-display-name"
            type="text"
            value={formData.display_name}
            onInput={(e) => setFormData({ ...formData, display_name: e.currentTarget.value })}
            placeholder="e.g., Main Website, Reddit Account"
          />
        </div>

        <div>
          <label htmlFor="surface-profile" className="block text-sm font-medium text-slate-700">
            {isBluesky ? "Bluesky handle or profile URL" : "URL"}
            {isBluesky && <span className="text-error"> *</span>}
          </label>
          <Input
            id="surface-profile"
            type="text"
            value={formData.canonical_url}
            onInput={(e) => {
              setFormData({ ...formData, canonical_url: e.currentTarget.value });
              if (submitError) setSubmitError(null);
            }}
            className="mt-1"
            placeholder={isBluesky ? "@handle.bsky.social or https://bsky.app/profile/handle.bsky.social" : "https://..."}
            aria-describedby={isBluesky ? "bluesky-profile-help" : undefined}
          />
          {isBluesky && (
            <p id="bluesky-profile-help" className="mt-1 text-xs text-slate-500">
              Public profile only. HelloConvo resolves the stable DID; no connection or credentials are required.
            </p>
          )}
        </div>

        {!isBluesky && <div>
          <label className="block text-sm font-medium text-slate-700">External ID</label>
          <input
            type="text"
            value={formData.external_id}
            onChange={(e) => setFormData({ ...formData, external_id: e.target.value })}
            className="mt-1 block w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
            placeholder="Platform-specific identifier"
          />
        </div>}

        <div>
          <label className="block text-sm font-medium text-slate-700">Notes</label>
          <textarea
            value={formData.notes}
            onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
            className="mt-1 block w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
            rows={3}
            placeholder="A note for your team"
            aria-describedby="surface-notes-help"
          />
          <p id="surface-notes-help" className="mt-1 text-xs text-slate-500">
            Shown on this surface&apos;s details for your team. Agents do not read it.
          </p>
        </div>
      </div>

      {submitError && (
        <p role="alert" className="mt-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          {submitError}
        </p>
      )}

      <div className="mt-6 flex gap-3">
        <button
          onClick={() => void handleSubmit()}
          disabled={!formData.display_name.trim() || (isBluesky && !formData.canonical_url.trim()) || createSurface.isPending}
          className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:bg-slate-300"
        >
          {createSurface.isPending ? "Creating..." : "Create Surface"}
        </button>
        <button
          onClick={() => setStep("kind")}
          className="rounded-lg px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100"
        >
          Back
        </button>
        <button
          onClick={onClose}
          className="rounded-lg px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

export function BrandSurfaces() {
  const { brandId } = useParams<{ brandId: string }>();
  const { data, isLoading, error, refetch } = useSurfaces(brandId);
  const { data: identitiesData } = useIdentities(brandId);
  const retireSurface = useRetireSurface(brandId ?? "");

  const [selectedSurface, setSelectedSurface] = useState<BrandSurface | null>(null);
  const [showAddFlow, setShowAddFlow] = useState(false);

  const surfaces = data?.data ?? [];
  const identities = identitiesData?.data ?? [];

  // Group surfaces by category
  const surfacesByCategory = surfaces.reduce(
    (acc, surface) => {
      const cat = surface.category;
      if (!acc[cat]) acc[cat] = [];
      acc[cat].push(surface);
      return acc;
    },
    {} as Record<SurfaceCategory, BrandSurface[]>
  );

  // Separate root and child surfaces
  const rootSurfaces = surfaces.filter((s) => !s.parent_surface_id);
  const childSurfaces = surfaces.filter((s) => s.parent_surface_id);

  const getParentName = (parentId: string | null) => {
    if (!parentId) return undefined;
    const parent = surfaces.find((s) => s.id === parentId);
    return parent?.display_name;
  };

  const handleRetire = async () => {
    if (!selectedSurface) return;
    await retireSurface.mutateAsync(selectedSurface.id);
    setSelectedSurface(null);
  };

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="h-8 w-48 animate-pulse rounded bg-slate-200" />
        <div className="mt-6 space-y-4">
          <div className="h-32 animate-pulse rounded-lg bg-slate-100" />
          <div className="h-32 animate-pulse rounded-lg bg-slate-100" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="rounded-lg bg-red-50 p-4 text-red-700">
          <p className="font-medium">Failed to load surfaces</p>
          <p className="mt-1 text-sm">{error.message}</p>
          <button
            onClick={() => refetch()}
            className="mt-3 rounded-lg bg-red-100 px-3 py-1.5 text-sm font-medium text-red-800 hover:bg-red-200"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      <WorkspaceHeader
        eyebrow="Brand Workspace"
        title="Surfaces"
        description="Where your brand publishes, participates, and needs visibility."
        badges={(
          <span className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-semibold text-slate-600">
            {surfaces.length} surface{surfaces.length === 1 ? "" : "s"}
          </span>
        )}
        metadata={(
          <>
            <span className="rounded-full bg-white px-2.5 py-1 ring-1 ring-slate-200">
              Owned / participatory / discovery
            </span>
            <span className="rounded-full bg-white px-2.5 py-1 ring-1 ring-slate-200">
              {identities.length} identit{identities.length === 1 ? "y" : "ies"}
            </span>
          </>
        )}
        actions={(
          <button
            onClick={() => setShowAddFlow(true)}
            className="rounded-full bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800"
          >
            Add Surface
          </button>
        )}
      />

      {showAddFlow && (
        <div>
          <AddSurfaceFlow
            brandId={brandId ?? ""}
            onClose={() => setShowAddFlow(false)}
            onSuccess={() => {
              setShowAddFlow(false);
              refetch();
            }}
          />
        </div>
      )}

      {selectedSurface && !showAddFlow && (
        <div>
          <SurfaceDetail
            brandId={brandId ?? ""}
            surface={selectedSurface}
            onClose={() => setSelectedSurface(null)}
            onRetire={handleRetire}
          />
        </div>
      )}

      {!showAddFlow && !selectedSurface && (
        <div className="space-y-8">
          {surfaces.length === 0 ? (
            <div className="rounded-[20px] border border-dashed border-slate-300 bg-white p-8 text-center shadow-sm">
              <p className="text-slate-500">No surfaces yet</p>
              <p className="mt-1 text-sm text-slate-400">
                Add surfaces to track where your brand shows up
              </p>
            </div>
          ) : (
            (Object.keys(CATEGORY_LABELS) as SurfaceCategory[]).map((category) => {
              const categorySurfaces = surfacesByCategory[category] ?? [];
              if (categorySurfaces.length === 0) return null;

              return (
                <div key={category}>
                  <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-slate-500">
                    {CATEGORY_LABELS[category]}
                  </h3>
                  <div className="space-y-3">
                    {categorySurfaces
                      .filter((s) => !s.parent_surface_id)
                      .map((surface) => (
                        <div key={surface.id}>
                          <SurfaceCard
                            surface={surface}
                            onClick={() => setSelectedSurface(surface)}
                          />
                          {/* Child surfaces */}
                          {categorySurfaces
                            .filter((s) => s.parent_surface_id === surface.id)
                            .map((child) => (
                              <div key={child.id} className="mt-2">
                                <SurfaceCard
                                  surface={child}
                                  isChild
                                  parentName={surface.display_name}
                                  onClick={() => setSelectedSurface(child)}
                                />
                              </div>
                            ))}
                        </div>
                      ))}
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}

      {identities.length > 0 && !showAddFlow && !selectedSurface && (
        <div className="mt-8 border-t border-slate-200 pt-6">
          <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
            Identities ({identities.length})
          </h3>
          <p className="mt-1 text-xs text-slate-400">
            Who acts on your surfaces — managed separately
          </p>
        </div>
      )}
    </div>
  );
}
